import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ClipboardList, Send, MapPin, Users, Clock, Sparkles, Phone, Mail, Lock, Users2, Zap } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { agencyMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { StatusPill, LoadingScreen, EmptyState, inr } from "@/components/common";

export default function Leads() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState({ price_per_guard_month: 15000, supervisor_included: true, technology_included: "", replacement_sla_hours: 24, contract_terms: "12-month contract, monthly billing.", message: "" });

  const load = () => api.get("/leads").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/quotes", { requirement_id: active.id, ...q, price_per_guard_month: Number(q.price_per_guard_month), replacement_sla_hours: Number(q.replacement_sla_hours) });
      toast.success("Quotation submitted!"); setActive(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardShell menu={agencyMenu} title="Leads">
      <PageHeader title="Customer Requirements" subtitle="Requirements matched to your agency. Submit competitive quotations to win contracts." />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No open leads right now" hint="New requirements in your service areas will appear here automatically." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="card-lift bg-white border border-navy-200 rounded-xl p-5" data-testid={`lead-${r.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-heading font-semibold text-navy-900">{r.security_requirement}</h3><p className="text-sm text-navy-500">{r.company_name || "Individual"} · {r.property_type}</p></div>
                <Badge className="bg-brand-50 text-brand-600 hover:bg-brand-50 gap-1"><Sparkles size={12} /> {r.match_score}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-navy-600">
                <span className="flex items-center gap-1"><MapPin size={14} /> {r.location}</span>
                <span className="flex items-center gap-1"><Users size={14} /> {r.num_guards} guards</span>
                <span className="flex items-center gap-1"><Clock size={14} /> {r.shift_duration}h × {r.num_shifts}</span>
                <span>{r.contract_duration_months} months</span>
              </div>
              <div className="mt-2 text-xs" data-testid={`lead-quotecount-${r.id}`}>
                {r.quote_count > 0
                  ? <span className="inline-flex items-center gap-1 text-navy-500"><Users2 size={13} /> {r.quote_count} {r.quote_count === 1 ? "agency has" : "agencies have"} already quoted</span>
                  : <span className="inline-flex items-center gap-1 text-brand font-semibold"><Zap size={13} /> Be the first to quote</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {r.supervisor_required && <Badge variant="outline" className="border-navy-200 text-navy-500 text-xs">Supervisor</Badge>}
                {r.cctv_required && <Badge variant="outline" className="border-navy-200 text-navy-500 text-xs">CCTV</Badge>}
                {r.budget_range && <Badge variant="outline" className="border-navy-200 text-navy-500 text-xs">{r.budget_range}</Badge>}
              </div>
              <div className="mt-3 rounded-lg bg-navy-50 border border-navy-100 px-3 py-2.5 text-sm">
                {r.contact_unlocked ? (
                  <div className="space-y-1" data-testid={`lead-contact-${r.id}`}>
                    <p className="brand-label text-emerald-600 mb-1">Customer contact</p>
                    <a href={`tel:${r.mobile}`} className="flex items-center gap-2 text-navy-800 hover:text-brand"><Phone size={13} /> {r.mobile}</a>
                    {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-navy-800 hover:text-brand"><Mail size={13} /> {r.email}</a>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-navy-500" data-testid={`lead-contact-locked-${r.id}`}>
                    <Lock size={13} className="text-brand shrink-0" /> <span>Contact unlocked after you submit a quote</span>
                  </div>
                )}
              </div>
              <div className="mt-4">
                {r.already_quoted ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Quotation submitted</Badge> : (
                  <Button className="w-full bg-navy-900 hover:bg-navy-800" onClick={() => { setActive(r); setQ({ ...q, price_per_guard_month: 15000 }); }} data-testid={`quote-lead-${r.id}`}><Send size={15} className="mr-1" /> Submit Quotation</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Quote for {active?.security_requirement}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm text-navy-500 bg-navy-50 rounded-lg p-3">{active.num_guards} guards · {active.location} · {active.contract_duration_months} months</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Price per guard/month (₹)</Label><Input type="number" value={q.price_per_guard_month} onChange={(e) => setQ({ ...q, price_per_guard_month: e.target.value })} data-testid="q-price" /></div>
                <div><Label className="text-xs">Replacement SLA (hours)</Label><Input type="number" value={q.replacement_sla_hours} onChange={(e) => setQ({ ...q, replacement_sla_hours: e.target.value })} data-testid="q-sla" /></div>
              </div>
              <div className="text-sm text-navy-700">Total monthly: <span className="font-bold text-navy-900">{inr(Number(q.price_per_guard_month) * (active.num_guards || 1))}</span></div>
              <div><Label className="text-xs">Technology Included</Label><Input value={q.technology_included} onChange={(e) => setQ({ ...q, technology_included: e.target.value })} placeholder="e.g. CCTV Monitoring" data-testid="q-tech" /></div>
              <label className="flex items-center justify-between border border-navy-200 rounded-lg px-4 py-2.5"><span className="text-sm">Supervisor Included</span><Switch checked={q.supervisor_included} onCheckedChange={(v) => setQ({ ...q, supervisor_included: v })} data-testid="q-supervisor" /></label>
              <div><Label className="text-xs">Message</Label><Textarea rows={2} value={q.message} onChange={(e) => setQ({ ...q, message: e.target.value })} data-testid="q-message" /></div>
            </div>
          )}
          <DialogFooter><Button className="bg-brand hover:bg-brand-600" onClick={submit} data-testid="submit-quote-btn">Submit Quotation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
